import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { combineLatest, filter, map, take } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { environment } from '../../environments/environment';

function e2eEnabled(): boolean {
  return (
    !environment.production &&
    typeof window !== 'undefined' &&
    window.localStorage.getItem('E2E_TEST_AUTH') === '1'
  );
}

// Gates the /clients admin area on the namespaced `permissions` claim. This is
// defense-in-UX only: the API independently enforces admin access. Non-admins
// are redirected to the dashboard. Pair with authGuard, which handles the
// unauthenticated case.
export const adminGuard: CanActivateFn = (_route, _state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const decide = (isAdmin: boolean): true | UrlTree =>
    isAdmin ? true : router.createUrlTree(['/dashboard']);

  // Under the E2E bypass the Auth0 SDK never settles isLoading$, so gate
  // directly on isGlobalAdmin$ (which reads the E2E_TEST_ADMIN flag).
  if (e2eEnabled()) {
    return auth.isGlobalAdmin$.pipe(take(1), map(decide));
  }

  return combineLatest([auth.isLoading$, auth.isGlobalAdmin$]).pipe(
    filter(([loading]) => !loading),
    take(1),
    map(([, isAdmin]) => decide(isAdmin))
  );
};
